import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { CartItemType } from '../../utils/sharedTypes'
import {
  IperiaCartEligibility,
  allowedProducts,
  formatHours,
  keptHours,
  keptSelectionFits,
} from '../../utils/iperiaCartEligibility'

export type CallbackState = 'idle' | 'sending' | 'sent' | 'error'

type IperiaHoursDialogProps = {
  eligibility: IperiaCartEligibility | null
  items: CartItemType[]
  callback: CallbackState
  submitting: boolean
  onKeep: (productCodes: string[]) => void
  onCallback: () => void
  onClose: () => void
}

const CallbackSent = () => (
  <Alert severity="success">
    C&apos;est noté : un conseiller pédagogique va vous appeler pour faire le point sur votre situation.
  </Alert>
)

/** Le panier dépasse les heures IPERIA restantes (`partial`) ou aucune formation n'y rentre (`none`, `unavailable`).
 *
 * `partial` : l'assistante maternelle garde les formations qui tiennent dans ses heures, ou demande à être rappelée.
 * Sinon : un conseiller pédagogique la rappelle ; la demande part dès l'ouverture (`onCallback` côté page). */
const IperiaHoursDialog = ({
  eligibility,
  items,
  callback,
  submitting,
  onKeep,
  onCallback,
  onClose,
}: IperiaHoursDialogProps) => {
  const [kept, setKept] = useState<string[]>([])

  useEffect(() => {
    setKept([])
  }, [eligibility])

  if (!eligibility) return null

  const nameOf = (code: string) => items.find((item) => item.productCode === code)?.productName || code
  const remaining = formatHours(eligibility.remaining_hours)
  const year = eligibility.year ?? ''
  const choosing = eligibility.status === 'partial' && callback === 'idle'

  const toggle = (code: string) =>
    setKept((current) => (current.includes(code) ? current.filter((value) => value !== code) : [...current, code]))

  return (
    <Dialog open onClose={submitting || callback === 'sending' ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {eligibility.status === 'unavailable'
          ? 'Les inscriptions en ligne sont closes pour cette année'
          : eligibility.status === 'partial'
            ? `Il vous reste ${remaining} de formation financées par IPERIA en ${year}`
            : `Vos heures de formation IPERIA ${year} sont épuisées`}
      </DialogTitle>
      <DialogContent>
        {choosing && (
          <Stack spacing={2}>
            <Typography>
              Ces heures ne couvrent pas toutes les formations choisies. Gardez celles que vous voulez suivre :
            </Typography>
            <Box>
              {allowedProducts(eligibility).map((product) => (
                <FormControlLabel
                  key={product.product_code}
                  sx={{ display: 'flex' }}
                  control={
                    <Checkbox
                      checked={kept.includes(product.product_code)}
                      onChange={() => toggle(product.product_code)}
                    />
                  }
                  label={`${nameOf(product.product_code)} · ${formatHours(product.hours)}`}
                />
              ))}
            </Box>
            <Typography
              variant="body2"
              color={keptHours(eligibility, kept) > eligibility.remaining_hours ? 'error' : 'text.secondary'}
            >
              {`Sélection : ${formatHours(keptHours(eligibility, kept))} sur ${remaining} disponibles`}
            </Typography>
          </Stack>
        )}
        {!choosing && callback === 'sending' && (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} />
            <Typography>Nous transmettons votre demande…</Typography>
          </Stack>
        )}
        {!choosing && callback === 'sent' && <CallbackSent />}
        {!choosing && callback === 'error' && (
          <Alert severity="error">
            Votre demande n&apos;a pas pu être transmise. Réessayez dans un instant.
          </Alert>
        )}
        {!choosing && callback === 'idle' && (
          <Typography>Un conseiller pédagogique peut vous appeler pour faire le point sur votre situation.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        {choosing && (
          <>
            <Button onClick={onCallback} disabled={submitting}>
              Je préfère être rappelée
            </Button>
            <Button
              variant="contained"
              disabled={submitting || !keptSelectionFits(eligibility, kept)}
              onClick={() => onKeep(kept)}
            >
              {submitting ? 'Envoi…' : 'Continuer avec ces formations'}
            </Button>
          </>
        )}
        {!choosing && callback === 'error' && (
          <Button variant="contained" onClick={onCallback}>
            Réessayer
          </Button>
        )}
        {!choosing && callback === 'idle' && (
          <Button variant="contained" onClick={onCallback}>
            Être rappelée
          </Button>
        )}
        {!choosing && callback !== 'sending' && <Button onClick={onClose}>Fermer</Button>}
      </DialogActions>
    </Dialog>
  )
}

export default IperiaHoursDialog
